import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';

import { GBENSContext } from '../GBENSContainer';
import { deleteShipment, duplicateShipment, getEnsSummary } from '../../../api/GBENSDec';
import LoadingSpinner from '../../../components/LoadingSpinner';

import dayjs from 'dayjs';

import ModalTemplate from './_ModalTemplate';

export const SubmitModal = ({ open, onClose }) => {
  const { allValid, headerId } = useContext(GBENSContext);
  const [ensSummary, setEnsSummary] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (allValid && headerId && open) {
      const getSummary = async () => {
        const summary = await getEnsSummary(headerId);
        setEnsSummary(summary);
      };
      getSummary();
    }
  }, [allValid, headerId, open]);

  return allValid ? (
    <ModalTemplate
      open={open}
      title="Submitting Shipment"
      text="Your VALID declaration(s) have been submitted to HMRC's Safety and Security system."
      body={
        <div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <div>Your reference:</div>
            <div>{ensSummary?.headerInfo?.clientJobNumber}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <div>Date:</div>
            <div>{dayjs().format('DD/MM/YY')}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <div>Time:</div>
            <div>{dayjs().format('HH:mm')}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <div>Consignments:</div>
            <div>{ensSummary?.headerInfo?.consignmentCount}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <div>Errors:</div>
            <div>{ensSummary?.headerInfo?.errorCount}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <div>Submitted:</div>
            <div>{ensSummary?.headerInfo?.submittedCount}</div>
          </div>
          <div>Please check all your consignments have been accepted and received an MRN</div>
        </div>
      }
      buttons={[
        <div key={0} />,
        <Button key={1} onClick={() => navigate('/Dashboard')}>
          Close
        </Button>,
      ]}
    />
  ) : (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Error"
      text="There was an error with your declaration(s). Please review and try again."
      body={<div />}
      buttons={[
        <div key={0} />,
        <Button key={1} onClick={onClose}>
          Close
        </Button>,
      ]}
    />
  );
};
SubmitModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export const DeleteConsignmentModal = ({ open, onClose }) => {
  const { deleteConsignmentRowsSelected, setDeleteConsignmentRowsSelected } = useContext(GBENSContext);
  const apiRef = useGridApiRef();

  const onSelect = () => {
    console.log(Object.values(Object.fromEntries(apiRef.current.getSelectedRows())).map((v) => v.id));
  };
  const columnsConsignments = [
    { field: 'reference', headerName: 'Reference', width: 180 },
    { field: 'consignor', headerName: 'Consignor', width: 180 },
    { field: 'consignee', headerName: 'Consignee', width: 180 },
  ];

  const rowsConsignments = [{ id: 1, reference: 'ABC', consignor: 'ABC LTD', consignee: 'DEF LTD' }];

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Delete a consignment"
      text="Please tick the consignments you would like to delete from this declaration."
      body={
        <DataGrid
          apiRef={apiRef}
          checkboxSelection
          onRowSelectionModelChange={(ids) => {
            setDeleteConsignmentRowsSelected(ids.length > 0);
          }}
          rows={rowsConsignments}
          columns={columnsConsignments}
        />
      }
      buttons={[
        <Button key={0} onClick={onClose}>
          Close
        </Button>,
        deleteConsignmentRowsSelected ? (
          <Button key={1} onClick={onSelect}>
            Delete
          </Button>
        ) : (
          <div />
        ),
      ]}
    />
  );
};

DeleteConsignmentModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export const DuplicateModal = ({ open, onClose, headerId }) => {
  const [jobReference, setJobReference] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setJobReference('');
    setError(null);
    setIsSubmitting(false);
  }, [open]);

  const duplicate = async () => {
    const _error = !jobReference ? 'Please  enter a job reference' : null;
    if (_error) setError(_error);
    else {
      try {
        setIsSubmitting(true);
        await duplicateShipment(headerId, jobReference);
        navigate('/Dashboard');
      } catch (e) {
        console.log(e);
      } finally {
        setIsSubmitting(false);
      }
      onClose();
    }
  };

  const duplicateShipmentForm = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        margin: 20,
      }}
    >
      <div style={{ width: '25%', fontWeight: 'bold', marginLeft: 30 }}>Job Reference</div>
      <div style={{ width: '70%', backgroundColor: 'white', padding: 15 }}>
        <TextField
          fullWidth
          value={jobReference}
          error={!!error}
          helperText={error || null}
          onChange={(e) => setJobReference(e.target.value)}
          variant="standard"
        />
      </div>
    </div>
  );

  const loadingScreen = (
    <>
      <div style={{ textAlign: 'center', paddingTop: 20 }}>Duplicating Shipment, please wait...</div>
      <LoadingSpinner />
    </>
  );

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Duplicate A Shipment"
      text={!isSubmitting && 'To duplicate a shipment and consignment details please enter a new job reference'}
      body={isSubmitting ? loadingScreen : duplicateShipmentForm}
      footerText={
        !isSubmitting && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 0 }}>
            Please note, the Vehicle ID and Date/Time details will not be duplicated.
          </div>
        )
      }
      buttons={[
        <Button key={0} onClick={onClose}>
          Close
        </Button>,
        <Button key={1} onClick={duplicate} className="duplicate-button" disabled={isSubmitting}>
          Duplicate
        </Button>,
      ]}
    />
  );
};

DuplicateModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  headerId: PropTypes.string,
};

export const DeleteShipmentModal = ({ open, onClose, headerId, reference }) => {
  const navigate = useNavigate();
  const deleteShipmentCall = () => {
    try {
      deleteShipment(headerId);
      navigate('/Dashboard');
    } catch (e) {
      console.log(e);
    }
    onClose();
  };

  const textDetails = reference
    ? `Do you want to delete ENS declaration ${reference}?`
    : 'Do you want to delete this ENS declaration?';
  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Delete This ENS Declaration"
      text={textDetails}
      body={
        <div className="delete-text">
          This action will delete the ENS declaration and all consignments associated with it.
        </div>
      }
      buttons={[
        <Button className="cancel-button-delete" key={0} onClick={onClose}>
          Cancel
        </Button>,
        <Button key={1} className="delete-button" onClick={deleteShipmentCall}>
          Delete ENS
        </Button>,
      ]}
    />
  );
};

DeleteShipmentModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  headerId: PropTypes.string,
  reference: PropTypes.string,
};

export const CannotDeleteShipmentModal = ({ open, onClose }) => {
  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Unable to delete this ENS / Consignment"
      text="Warning:"
      body={
        <div>
          <div>Items on this consignment have been submitted to HMRC and this declaration is locked.</div>
          <div>Please try again when the responses have been received.</div>
        </div>
      }
      buttons={[
        <div key={0} />,
        <Button key={1} onClick={onClose}>
          Cancel
        </Button>,
      ]}
    />
  );
};

CannotDeleteShipmentModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
